import React, { Component } from 'react';
import get from 'lodash/get';
import Logger from 'observability/Logger';

import {
    PUBSUB_TAB_CHANGE_LAYOUT,
    PUBSUB_TAB_CHANGE_DESIGN,
    PUBSUB_CONTEXT,
} from 'common/components/CmsIframe/constants';

const LOG = new Logger('withCmsSidebarState');

// These are the constants used by the CMS that refer to the two tabs.
export const TAB_LAYOUT = 'content';
export const TAB_DESIGN = 'theme';

/**
 * Given the top level iframe from a /sites/view/splashdomain edit page,
 * safely reach in and return the function from the sidebar-manager.js asset,
 * loaded inside of that iframe, that opens a given sidebar tab.
 *
 * @param {Object} window - the type of object returned by
 *                          document.querySelector('iframe').contentWindow
 * @return {function} (tab) => void
 *                          a function that will open the given sidebar tab
 */
const getOpenCmsSidebarFunction = (iframeWindow) => {
    const openSidebarFunc = get(iframeWindow, 'splash.sidebarManager.openSidebar');
    const sidebarManager = get(iframeWindow, 'splash.sidebarManager');

    if (!openSidebarFunc) {
        LOG.warn('No splash.sidebarManager.openSidebar to call');
        return () => {};
    }

    return (tabName) => openSidebarFunc.bind(sidebarManager)(tabName, { force: true });
};

/**
 * Wrap your CMS Iframe component with this HOC to give it three extra props
 *
 *  - one to initialize the cms sidebar when the iframes load
 *  - one to change the cms sidebar tab
 *  - and one indicating what tab is open
 *
 */
const withCmsSidebarState = (WrappedComponent) =>
    class ComponentWithCmsSidebarState extends Component {
        state = {
            currentCmsSidebarTab: null,
        };

        initializeCmsSidebar = (iframeWindow, tab = TAB_LAYOUT) => {
            this.iframeWindow = iframeWindow;

            this.subscribeToCurrentTabState(iframeWindow);

            this.changeCmsSidebarTab(tab);
        };

        changeCmsSidebarTab = (tab) => {
            if (tab) {
                getOpenCmsSidebarFunction(this.iframeWindow)(tab);
            }

            this.setState({ currentCmsSidebarTab: tab });
        };

        /**
         * Keep the state in sync with the CMS
         * @param  {window} iframeWindow
         */
        subscribeToCurrentTabState = (iframeWindow) => {
            const PubSub = get(iframeWindow, 'splash.PubSub');

            if (!PubSub) {
                return;
            }

            PubSub.subscribe(PUBSUB_TAB_CHANGE_LAYOUT, {
                context: PUBSUB_CONTEXT,
                eventHandler: () => this.setState({ currentCmsSidebarTab: TAB_LAYOUT }),
            });
            PubSub.subscribe(PUBSUB_TAB_CHANGE_DESIGN, {
                context: PUBSUB_CONTEXT,
                eventHandler: () => this.setState({ currentCmsSidebarTab: TAB_DESIGN }),
            });
        };

        render() {
            return (
                <WrappedComponent
                    initializeCmsSidebar={this.initializeCmsSidebar}
                    changeCmsSidebarTab={this.changeCmsSidebarTab}
                    currentCmsSidebarTab={this.state.currentCmsSidebarTab}
                    {...this.props}
                />
            );
        }
    };

export default withCmsSidebarState;
